/** @jsxImportSource @emotion/react */

import { Chip, css, Divider, Grid, Tooltip, Typography } from "@mui/material";
import { PALETTE } from "styles/base/variables";
import { ReactComponent as IconDownload } from "assets/img/icon/download.svg";
import IconButton from "components/common/Button/Icon";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Business, MenuType, StoreType, SubType } from "models/business.model";
import { downloadNewzenExcel } from "pages/remotes";
import { useState } from "react";
import ReportButton from "components/Report/Button";
import { convertMenuTypeToStoreType, extractSubType } from "./converter";
import CircleIcon from "@mui/icons-material/Circle";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Journal, JournalStatus } from "models/journal.model";
import {
  holdReasonLabel,
  journalStatus,
  STATUS_LABELS
} from "pages/Journal/journal.logic";

const ProgressBar = ({
  total,
  progressed,
  color,
}: {
  total: number;
  progressed: number;
  color?: string;
}) => {
  const percentage = total ? (progressed / total) * 100 : 0;

  return (
    <div
      css={css`
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 4px;
      `}
    >
      <div
        css={css`
          width: 120px;
          height: 6px;
          background-color: ${total
            ? PALETTE.progressBackground
            : "transparent"};
          border-radius: 5px;
        `}
      >
        <div
          css={css`
            width: ${percentage}%;
            height: 100%;
            background-color: ${color || PALETTE.progressCompleted};
            border-radius: 5px;
          `}
        />
      </div>
      <span
        css={css`
          font-size: 12px;
        `}
      >
        {total ? percentage.toFixed(0) + "%" : "-"}
      </span>
      {total ? (
        <span
          css={css`
            margin-left: 4px;
            font-size: 12px;
            white-space: nowrap;
          `}
        >
          (
          <span
            css={css`
              ${!progressed && "color: red;"}
            `}
          >
            {progressed}
          </span>
          /{total})
        </span>
      ) : (
        "-"
      )}
    </div>
  );
};

export const renderProgressBar = (
  total: string,
  completed: string,
  newzenTransferred: string
) => {
  return (
    <Grid display="flex" flexDirection="column" gap={0.5}>
      <ProgressBar total={+total} progressed={+completed} />
      <ProgressBar
        total={+total}
        progressed={+newzenTransferred}
        color={PALETTE.primary}
      />
    </Grid>
  );
};

export const twoLineColumn = (firstLine: string, secondLine: string) => {
  return (
    <Grid display="flex" flexDirection="column" gap={0.4}>
      <Grid fontSize={13}>{firstLine}</Grid>
      <Grid fontSize={13}>{secondLine}</Grid>
    </Grid>
  );
};

export const renderDateTime = (dateTime?: string | null) => {
  if (!dateTime) return "-";

  const [datePart, timePart] = dateTime.split("T");
  const time = timePart ? timePart.slice(0, 8) : "";

  return (
    <Grid display="flex" flexDirection="column" alignItems="flex-end" gap={0.4}>
      <Grid fontSize={13}>{datePart}</Grid>
      <Grid fontSize={12}>{time}</Grid>
    </Grid>
  );
};

export const 진행상태와색상표기 = () => {
  return (
    <Grid display="flex" alignItems="center">
      진행상태
      <Grid
        css={css`
          display: flex;
          align-items: center;
          margin-left: 4px;
          font-size: 14px;
        `}
      >
        (
        <CircleIcon
          css={css`
            width: 16px;
            margin-left: 2px;
            margin-right: 2px;
            fill: ${PALETTE.progressCompleted};
          `}
        />
        작업량
        <CircleIcon
          css={css`
            width: 16px;
            margin-left: 6px;
            margin-right: 2px;
            fill: ${PALETTE.primary};
          `}
        />
        전송량 )
      </Grid>
    </Grid>
  );
};

export const renderProgressText = (
  total: string,
  completed: string,
  newzenTransferred: string
) => {
  return (
    <Grid
      display="flex"
      alignItems="center"
      justifyContent="flex-end"
      gap={0.5}
    >
      <Grid display="flex" flexDirection="column" gap="4px">
        <Grid fontSize={12}>{`${completed}`}</Grid>
        <Grid fontSize={12}>{`${newzenTransferred}`}</Grid>
      </Grid>
      <Grid marginLeft="3px" display="flex" alignItems="center" columnGap="4px">
        <Divider
          orientation="vertical"
          flexItem
          sx={{ borderColor: PALETTE.border, height: 28 }}
        />
        <Typography fontSize={13} minWidth="24px">
          {total}
        </Typography>
      </Grid>
    </Grid>
  );
};

export const RenderLink = (path: string, id: string, label: string) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [startDate, endDate] = [
    searchParams.get("startDate"),
    searchParams.get("endDate"),
  ];

  let reviewLink = `/${path}/${id}`;
  if (startDate && endDate)
    reviewLink += `?startDate=${startDate}&endDate=${endDate}`;

  return <Link to={reviewLink}>{label}</Link>;
};

interface BusinessWithLoadData extends Business {
  loadData: () => void;
}

export const DashboardAction = (data: BusinessWithLoadData) => {
  const location = useLocation();
  const paths = location.pathname.split("/");
  const menuType = paths[paths.length - 1] as MenuType;
  const storeType: StoreType = convertMenuTypeToStoreType(menuType);

  const [searchParams, setSearchParams] = useSearchParams();
  const [startDate, endDate] = [
    searchParams.get("startDate"),
    searchParams.get("endDate"),
  ];
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);

  if (!(startDate && endDate)) {
    alert("기간이 없습니다.");
    return null;
  }

  const bizId = data.id;
  const noData = !data.totalDataNumber;

  const handleDownload = () => {
    const paramData = { startDate, endDate };

    setIsDownloadLoading(true);
    downloadNewzenExcel(storeType, bizId, paramData)
      .then((res) => {
        setIsDownloadLoading(false);
        renderDownload(res);
      })
      .catch((err) => {
        setIsDownloadLoading(false);
        console.error(err);
      });
  };

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
      `}
    >
      <IconButton
        disabled={noData || isDownloadLoading}
        onClick={handleDownload}
      >
        <IconDownload
          css={css`
            stroke: ${noData || isDownloadLoading
              ? PALETTE.iconDisabled
              : PALETTE.primary};
          `}
        />
      </IconButton>
    </div>
  );
};

export const renderDownload = (res: any) => {
  const contentDisposition = res.headers?.get("content-disposition");
  const filename =
    contentDisposition?.split(";")[1].split("=")[1].replace(/"/g, "") ||
    "파일명없음";

  if (filename && res.data) {
    const blob = new Blob([res.data]);
    const excelUrl = window.URL.createObjectURL(blob);
    const excelLink = document.createElement("a");
    excelLink.href = excelUrl;
    excelLink.download = decodeURI(filename);
    excelLink.click();
  }
};

export interface ChipAppearance {
  color?: "primary" | "white" | "gray";
  variant?: "filled" | "outlined";
  shape?: "rounded" | "square";
}

export const renderChip = (
  label: string,
  appearance: ChipAppearance = {
    color: "primary",
    variant: "filled",
    shape: "rounded",
  }
) => {
  return (
    <Chip
      label={label}
      variant={appearance.variant}
      sx={{
        height: 24,
        fontSize: 12,
        fontWeight: 600,
        padding: "0",
        borderRadius: appearance.shape === "rounded" ? "12px" : "4px",
        ...(appearance.color === "primary" &&
          appearance.variant === "filled" && {
            backgroundColor: PALETTE.primary,
            color: PALETTE.light,
          }),
        ...(appearance.color === "white" &&
          appearance.variant === "filled" && {
            backgroundColor: PALETTE.light,
            color: PALETTE.text,
          }),
        ...(appearance.color === "gray" &&
          appearance.variant === "filled" && {
            backgroundColor: PALETTE.borderLight,
            color: PALETTE.text,
          }),
        ...(appearance.color === "primary" &&
          appearance.variant === "outlined" && {
            border: `1px solid ${PALETTE.primary}`,
            color: PALETTE.primary,
          }),
        ...(appearance.color === "gray" &&
          appearance.variant === "outlined" && {
            border: `1px solid ${PALETTE.borderLight}`,
            color: PALETTE.borderLight,
          }),
      }}
    />
  );
};

const STATUS_TEXT_COLORS: Record<JournalStatus, string> = {
  unsent: PALETTE.text,
  held: PALETTE.warning,
  sent: PALETTE.progressCompleted,
  failed: PALETTE.error
};

// 기타매입·매출의 전송상태 열처럼 글자로 표시하되, 상태별 색과 보조 정보(전표번호·보류 사유·실패 사유)를 붙인다
export const renderJournalStatus = (journal: Journal) => {
  const status = journalStatus(journal);
  const label =
    status === "sent" && journal.newzenSlipNo
      ? `전송 #${journal.newzenSlipNo}`
      : STATUS_LABELS[status];
  const sub =
    status === "held"
      ? holdReasonLabel(journal.holdReason)
      : status === "failed"
      ? journal.newzenErrorMessage || journal.newzenErrorRule || ""
      : "";

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: 4px;
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
        `}
      >
        <span
          css={css`
            color: ${STATUS_TEXT_COLORS[status]};
            font-weight: ${status === "unsent" ? 400 : 500};
            white-space: nowrap;
          `}
        >
          {label}
        </span>
        {sub && (
          <Tooltip title={sub}>
            <span
              css={css`
                max-width: 120px;
                font-size: 12px;
                color: ${PALETTE.textMid};
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              `}
            >
              {sub}
            </span>
          </Tooltip>
        )}
      </div>
      {journal.isAccountingConfirmed && (
        <Tooltip title="마감됨">
          <LockOutlinedIcon
            css={css`
              font-size: 16px;
              color: ${PALETTE.icon};
            `}
          />
        </Tooltip>
      )}
    </div>
  );
};
